import type {Component, Editor} from 'grapesjs';
import {colorToHex, getFormattedHexColor, isCorrectHexFormat} from '@Utils/color.util';
import {setComponentStyle, setTextColorStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {ColorControlDescriptor} from './color-control.types';
import {DEFAULT_COLOR, TEXT_BASED_COMPONENT_TYPES} from './color-control.types';

/** Returns the raw hex WITHOUT '#', or undefined when no color is set (empty or 'transparent'). */
export function getFormattedColor(rawValue: string): string | undefined {
  if (!rawValue || rawValue === 'transparent') {
    return undefined;
  }
  if (isCorrectHexFormat(rawValue)) {
    return rawValue;
  }
  return getFormattedHexColor(colorToHex(rawValue));
}

function ensureLeadingHash(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

function isTextBasedType(type: string): boolean {
  return TEXT_BASED_COMPONENT_TYPES.has(type);
}

/** The `#RRGGBB` swatch color to show for a descriptor on a component: the live value, or the descriptor default. */
export function getDisplayHex(rawValue: string, descriptor: ColorControlDescriptor): string {
  return `#${getFormattedColor(rawValue) ?? descriptor.defaultColor ?? DEFAULT_COLOR}`;
}

/**
 * Applies a color to the descriptor's target, routing through the descriptor's onColorChange side effect and
 * then either the text-color or generic component-style writer. Shared by the inline control and the overlay.
 */
export function applyDescriptorColor(editor: Editor, component: Component, descriptor: ColorControlDescriptor, rawColor: string): void {
  const hexWithHash = ensureLeadingHash(rawColor);
  if (descriptor.onColorChange) {
    descriptor.onColorChange(hexWithHash, component, editor);
  }
  const componentType = component.get('type');
  if (descriptor.isTextColor && typeof componentType === 'string' && isTextBasedType(componentType)) {
    setTextColorStyle(editor, hexWithHash, component);
  } else {
    setComponentStyle(editor, {property: descriptor.property, value: hexWithHash, component});
  }
}
